import { LicensingAgencyStatus } from '@/@types/generated'
import EnumSelect from '@/components/Atoms/EnumSelect'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { useError } from '@/hooks/useError'
import format from '@/lib/format'
import { CompletedLicensingAgencyStatuses } from '@/modules/applications/modules/licensing-agency/lib/licensing-agency-status'
import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangleIcon, ArrowRightIcon } from 'lucide-react'
import { PropsWithChildren, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { LicensingAgencyStatusBadge } from '../LicensingAgencyStatusBadge'

interface LicensingAgencyUpdateStatusDialogProps {
  // Current status
  status?: LicensingAgencyStatus
  statusOptions?: LicensingAgencyStatus[]
  onSubmit: (status: LicensingAgencyStatus) => Promise<void>
}

const { ACTIVE } = LicensingAgencyStatus

export const LicensingAgencyUpdateStatusDialog = ({
  status,
  statusOptions = CompletedLicensingAgencyStatuses,
  onSubmit,
  children,
}: PropsWithChildren<LicensingAgencyUpdateStatusDialogProps>) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const { showSuccess, showError } = useError()

  const form = useForm<TFormSchema>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      status,
    },
  })

  const _onSubmit = async ({ status }: TFormSchema) => {
    try {
      setSaving(true)

      // Call confirm handler
      await onSubmit(status)

      showSuccess(`Status updated successfully`)
      setOpen(false)
    } catch (error) {
      showError(error)
    } finally {
      setSaving(false)
    }
  }

  // If status is changed on the parent, sync with form
  useEffect(() => {
    if (!status) return
    form.setValue('status', status)
  }, [status, form])

  // Reset the form when the dialog is closed
  useEffect(() => {
    if (open) return
    form.reset()
  }, [form, open])

  const newStatus = form.watch('status')
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex flex-col md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Licensing Agency Status</DialogTitle>
          <DialogDescription className="text-left">
            Update the licensing agency status. This will affect the company's
            ability to manage certificates, vehicles, renewals, and payments on
            behalf of their authorized companies.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(_onSubmit)}>
            <div className="mb-4 grid items-start gap-4 sm:grid-cols-[repeat(3,minmax(0,max-content))]">
              <div className="grid gap-2">
                <Label>Current Status</Label>
                <div className="flex min-h-9 items-center text-sm">
                  {format.Enum(LicensingAgencyStatus, status)}
                </div>
              </div>

              <div className="flex min-h-9 items-center self-end">
                <ArrowRightIcon />
              </div>

              <div className="grid gap-2">
                <Label>New Status</Label>
                <Controller
                  control={form.control}
                  name="status"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <EnumSelect
                      options={statusOptions}
                      itemFormatter={(v) => (
                        <LicensingAgencyStatusBadge status={v} />
                      )}
                      value={field.value}
                      onChange={field.onChange}
                      className="justify-self-start"
                      single
                      required
                    />
                  )}
                />
              </div>
            </div>

            {newStatus === ACTIVE && status != ACTIVE && (
              <Alert variant="success">
                <AlertTriangleIcon />
                <AlertTitle>Confirm Authorization</AlertTitle>
                <AlertDescription>
                  Clicking "Save" below will grant this company authorization to
                  manage certificates, vehicles, renewals, and payments on
                  behalf of companies who authorize them.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter className="mt-4 md:flex-row-reverse">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={saving || newStatus === status}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

type TFormSchema = z.infer<typeof FormSchema>
const FormSchema = z.object({
  status: z.enum(LicensingAgencyStatus),
})
